import { useEffect, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import Hero from 'components/Hero';
import BoatForm from 'components/forms/BoatForm';
import StickyBar from 'components/StickyBar';
import { useBreakpoint } from 'components/BreakpointProvider';
import { useRouter } from 'next/router';
import { useUserDetails } from 'auth/mutations/userHooks';
import Backdrop from 'components/remodal/v2/Backdrop';
import styled from 'styled-components';
import { mediaMSmMax } from 'helpers/breakpoints';
import ConnectBlwLayout from '../../../src/app/components/layouts/ConnectBlwLayout';

const EntryPage = () => {
  const { isAuthenticated } = useUserDetails();

  const [showButton, setShowButton] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const breakpoints = useBreakpoint();
  const smallScreen = breakpoints.xs || breakpoints.sm;

  useEffect(() => {
    setShowForm(!smallScreen);
    return () => {
      document.body.classList.remove('slider-open');
    };
  }, [smallScreen]);

  const openSliderForm = (open) => {
    setShowForm(open);
    if (!showForm) {
      document.body.classList.add('slider-open');
    } else {
      document.body.classList.remove('slider-open');
    }
  };

  const params = useRouter();

  useEffect(() => {
    if (params.query.openModal && smallScreen) {
      setTimeout(() => {
        openSliderForm(true);
      }, 300);
    }
  }, [params.query, smallScreen]);

  const modalRef = useRef(null);

  useEffect(() => {
    const windowHeight = window.innerHeight;
    if (showForm) {
      const modalHeight = modalRef?.current?.offsetHeight;
      const animateHeight = 100 - (modalHeight * 100) / windowHeight - 7;
      document.body.setAttribute('style', `--boatform-height: ${animateHeight}vh`);
    }
  }, [showForm]);

  return (
    <ConnectBlwLayout name="home" isHome isProtected={false}>
      <>
        <CSSTransition in={showForm && smallScreen} timeout={300} unmountOnExit classNames="remodal-overlay">
          <Backdrop>&nbsp;</Backdrop>
        </CSSTransition>
        <div className="Grid">
          <div
            className={`BLWHero u-md-pt1 Grid-cell u-ip-fullSize u-lg-min-pr2 u-lg-min-half ${
              isAuthenticated ? 'u-lg-size3of5' : ' u-lg-size2of5'
            } u-flex  u-flexAlignItemsStart`}
          >
            <Hero isAuthenticated={isAuthenticated} />
          </div>
          {!isAuthenticated && (
            <div className="Grid-cell u-sm-hide u-md-hide u-lg-min-hide u-lg-min-none u-lg-size1of5 Grid-cell-sm--padding u-flex u-flexAlignItemsCenter" />
          )}
          <BoatFormWrapper
            id="BoatForm"
            className={`Grid-cell u-ip-fullSize u-lg-min-half ${
              isAuthenticated ? 'u-lg-size2of5' : ' u-lg-size2of5'
            } Grid-cell-sm--padding u-flex u-flexAlignItemsCenter`}
          >
            <CSSTransition
              in={showForm}
              timeout={300}
              classNames="BoatForm"
              unmountOnExit
              mountOnEnter
              onEnter={() => setShowButton(false)}
              onExit={() => setShowButton(true)}
            >
              <BoatForm
                modalRef={modalRef}
                showForm={showForm}
                setShowForm={openSliderForm}
                smallScreen={smallScreen}
              />
            </CSSTransition>
          </BoatFormWrapper>
          <CSSTransition in={showButton} timeout={300} classNames="BoatForm-stickybar" unmountOnExit mountOnEnter>
            <StickyBar additionalClasses="BoatForm-stickybar u-sm-show">
              <div className="Grid Grid--alignMiddle">
                <div className="Grid-cell u-mt0">
                  <button
                    type="submit"
                    onClick={() => openSliderForm(true)}
                    className="Button Button--yellow  u-textBold u-fsSm u-textUpper u-sizeFull u-posRelative"
                  >
                    <span>Get Started</span>
                  </button>
                </div>
              </div>
            </StickyBar>
          </CSSTransition>
        </div>
      </>
    </ConnectBlwLayout>
  );
};

const BoatFormWrapper = styled.div`
  ${mediaMSmMax} {
    left: 0;
    right: 0;
    width: 100%;
    max-width: 100%;
    border: 0;
    padding: 0;
    margin: 0;
    border-radius: 0;
    z-index: 9999;

    .BoatForm-enter {
      transform: translateY(100vh);
    }

    .BoatForm-enter-active {
      transform: translateY(var(--boatform-height));
      transition: opacity 500ms cubic-bezier(0.19, 1, 0.22, 1), transform 500ms cubic-bezier(0.19, 1, 0.22, 1);
    }

    .BoatForm-enter-done {
      transform: translateY(0);
      transition: opacity 500ms cubic-bezier(0.19, 1, 0.22, 1), transform 500ms cubic-bezier(0.19, 1, 0.22, 1);
      position: fixed;
      bottom: 0;
      padding-bottom: 0;
    }

    .PanelHeightNone {
      transform: translateY(0);
      transition: opacity 500ms cubic-bezier(0.19, 1, 0.22, 1), transform 500ms cubic-bezier(0.19, 1, 0.22, 1);

      position: fixed;
      bottom: 0;
      padding-bottom: 0;
    }

    .TopInputFocus {
      position: fixed;
      top: -100px;
    }
    .TopInputFocusAddress {
      position: fixed;
      top: 10px;
    }

    .BoatForm-exit {
      transform: translateY(var(--boatform-height));
      transition: opacity 500ms cubic-bezier(0.19, 1, 0.22, 1), transform 500ms cubic-bezier(0.19, 1, 0.22, 1);
    }
    .BoatForm-exit-active {
      transform: translateY(100vh);
      transition: opacity 500ms cubic-bezier(0.19, 1, 0.22, 1), transform 500ms cubic-bezier(0.19, 1, 0.22, 1);
    }

    .BoatForm-exit-done {
      transform: translateY(100vh);
      transition: opacity 500ms cubic-bezier(0.19, 1, 0.22, 1), transform 500ms cubic-bezier(0.19, 1, 0.22, 1);
    }

    .BoatForm-stickybar {
      z-index: 98;
    }

    .BoatForm-stickybar-enter {
      transform: translateY(87px);
    }
    .BoatForm-stickybar-enter-active {
      transform: translateY(var(0));
      transition: opacity 500ms cubic-bezier(0.19, 1, 0.22, 1), transform 500ms cubic-bezier(0.19, 1, 0.22, 1);
    }
    .BoatForm-stickybar-exit {
      transform: translateY(var(0));
      transition: opacity 500ms cubic-bezier(0.19, 1, 0.22, 1), transform 500ms cubic-bezier(0.19, 1, 0.22, 1);
    }
    .BoatForm-stickybar-exit-active {
      transform: translateY(87px);
      transition: opacity 500ms cubic-bezier(0.19, 1, 0.22, 1), transform 500ms cubic-bezier(0.19, 1, 0.22, 1);
    }
  }
`;

export default EntryPage;
